import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, Clock, BookOpen, Quote, ChevronDown, Plus, Minus, Expand, Minimize } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { INSIGHTS } from '../mock';
import { CONTENT } from '../data/insightContent';
import InsightMotif from '../components/InsightMotif';
import ClosingCTA from '../components/ClosingCTA';
import SEO from '../components/SEO';
import { track } from '../lib/analytics';

// A section body may contain \n\n paragraph breaks — render them as <p>s.
const Body = ({ text }) => {
  if (!text) return null;
  const paragraphs = text.split(/\n{2,}/).filter(Boolean);
  return (
    <>
      {paragraphs.map((p, i) => (
        <p key={i} className="text-[16px] md:text-[17px] leading-[1.75] text-[#1A1A1A]/85 mt-4 first:mt-0 whitespace-pre-line">
          {p}
        </p>
      ))}
    </>
  );
};

function ChapterAccordion({ chapter, index, open, onToggle, chapterId }) {
  const sectionsCount = chapter.sections?.length || 0;
  return (
    <section id={chapterId} className="scroll-mt-24 border-t border-[#E8E4D9]">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`${chapterId}-panel`}
        data-testid={`chapter-toggle-${index}`}
        className="w-full text-left flex items-start gap-6 py-6 md:py-8 group"
      >
        <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#22A94F] pt-2 shrink-0 w-16">
          Ch.{String(index + 1).padStart(2, '0')}
        </div>
        <div className="flex-1">
          <h2 className="serif-tight text-[26px] md:text-[36px] leading-[1.1] text-[#1A1A1A] group-hover:text-[#0F3D22] transition-colors">
            {chapter.title}
          </h2>
          {sectionsCount > 0 && (
            <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#1A1A1A]/45 mt-2">
              {sectionsCount} {sectionsCount === 1 ? 'section' : 'sections'}
            </div>
          )}
        </div>
        <div
          className={`shrink-0 w-10 h-10 rounded-full border border-[#E8E4D9] flex items-center justify-center transition-transform duration-300 ${open ? 'rotate-180 bg-[#0F3D22] text-[#7ED4A0] border-[#0F3D22]' : 'text-[#1A1A1A]/60'}`}
          aria-hidden="true"
        >
          <ChevronDown size={18} />
        </div>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={`${chapterId}-panel`}
            role="region"
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            style={{ overflow: 'hidden' }}
          >
            <div className="pl-0 md:pl-[88px] pr-0 pb-10 md:pb-14">
              {chapter.sections?.map((s, si) => (
                <div key={si} className={si === 0 ? '' : 'mt-8 md:mt-10 pt-8 md:pt-10 border-t border-[#E8E4D9]/60'}>
                  {s.heading && (
                    <h3 className="serif text-[20px] md:text-[24px] text-[#0F3D22] leading-[1.2] mb-3">
                      {s.heading}
                    </h3>
                  )}
                  <Body text={s.body} />
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

export default function InsightPost() {
  const { slug } = useParams();
  const meta = INSIGHTS.find((x) => x.slug === slug);
  const body = CONTENT[slug];
  const others = useMemo(() => INSIGHTS.filter((x) => x.slug !== slug).slice(0, 3), [slug]);

  // Support legacy `sections` format by wrapping in a single chapter.
  const chapters = useMemo(() => {
    if (!body) return [];
    if (body.chapters) return body.chapters;
    if (body.sections) {
      return body.sections.map((s) => ({ title: s.h, sections: [{ heading: null, body: s.b }] }));
    }
    return [];
  }, [body]);

  // First chapter open by default, others closed.
  const [openSet, setOpenSet] = useState(() => new Set([0]));

  // Reset accordion state when slug changes.
  useEffect(() => {
    setOpenSet(new Set([0]));
    window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
  }, [slug]);

  if (!meta) return <Navigate to="/insights" replace />;

  const allOpen = openSet.size === chapters.length && chapters.length > 0;

  const toggle = (idx) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else {
        next.add(idx);
        track('Insight Chapter Opened', { slug, chapter_index: idx, chapter_title: chapters[idx]?.title || '' });
      }
      return next;
    });
  };

  const expandAll = () => setOpenSet(new Set(chapters.map((_, i) => i)));
  const collapseAll = () => setOpenSet(new Set());

  const jumpTo = (idx) => {
    setOpenSet((prev) => new Set([...prev, idx]));
    // Wait for panel to expand before scrolling.
    requestAnimationFrame(() => {
      const el = document.getElementById(`chapter-${idx}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  return (
    <div>
      <SEO
        title={`${meta.title}${meta.subtitle ? ` — ${meta.subtitle}` : ''}`}
        description={meta.excerpt}
        canonical={`https://digitalworks.co/insights/${meta.slug}`}
        ogImage={`/og-cards/insights/${meta.slug}.png`}
        type="article"
      />
      {/* HERO */}
      <section className="pt-16 pb-10">
        <div className="dwx">
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 mono text-[11px] tracking-[0.24em] uppercase text-[#1A1A1A]/60 hover:text-[#1A1A1A]"
            data-testid="insight-back-link"
          >
            <ArrowLeft size={14} /> All insights
          </Link>
          <div className="mt-8 grid grid-cols-12 gap-8 items-end">
            <div className="col-span-12 lg:col-span-7">
              <div className="mono text-[11px] uppercase tracking-[0.24em] text-[#22A94F]">
                {meta.category} · {meta.format}
              </div>
              <h1 className="serif-tight text-[46px] md:text-[76px] leading-[0.98] mt-4" data-testid="insight-title">
                {meta.title}
              </h1>
              {meta.subtitle && (
                <div className="serif text-[22px] md:text-[28px] text-[#1A1A1A]/60 mt-3">{meta.subtitle}</div>
              )}
              <div className="mt-6 flex flex-wrap items-center gap-6 text-[12px] mono uppercase text-[#1A1A1A]/55">
                <span className="inline-flex items-center gap-2"><Calendar size={13} />{meta.date}</span>
                <span className="inline-flex items-center gap-2"><Clock size={13} />{meta.readTime}</span>
                <span className="inline-flex items-center gap-2"><BookOpen size={13} />{meta.format}</span>
                <span>DigitalWorks Editorial</span>
              </div>
            </div>
            <div className="col-span-12 lg:col-span-5">
              <div className="rounded-[20px] bg-[#0F3D22] p-6 h-56 text-[#7ED4A0]">
                <InsightMotif variant={meta.motif} tone="#7ED4A0" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BODY */}
      <article className="pb-16">
        <div className="dwx grid grid-cols-12 gap-10">
          {/* Table of contents */}
          <aside className="hidden lg:block col-span-3">
            <div className="sticky top-24">
              <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#1A1A1A]/50 mb-4">In this book</div>
              <ul className="space-y-2 text-[13px]" data-testid="chapters-toc">
                {chapters.map((ch, i) => (
                  <li key={i}>
                    <button
                      type="button"
                      onClick={() => jumpTo(i)}
                      className="text-left text-[#1A1A1A]/70 hover:text-[#22A94F] transition-colors flex gap-2 leading-snug"
                      data-testid={`toc-item-${i}`}
                    >
                      <span className="mono opacity-50 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                      <span>{ch.title}</span>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-6 border-t border-[#E8E4D9] flex flex-wrap gap-2">
                {allOpen ? (
                  <button
                    type="button"
                    onClick={collapseAll}
                    className="inline-flex items-center gap-1.5 mono text-[10px] tracking-[0.2em] uppercase px-3 py-2 rounded-full bg-[#F4F2EC] hover:bg-[#E8E4D9] text-[#1A1A1A]/70"
                    data-testid="collapse-all-btn"
                  >
                    <Minimize size={12} /> Collapse all
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={expandAll}
                    className="inline-flex items-center gap-1.5 mono text-[10px] tracking-[0.2em] uppercase px-3 py-2 rounded-full bg-[#F4F2EC] hover:bg-[#E8E4D9] text-[#1A1A1A]/70"
                    data-testid="expand-all-btn"
                  >
                    <Expand size={12} /> Expand all
                  </button>
                )}
              </div>
            </div>
          </aside>

          {/* Article body */}
          <div className="col-span-12 lg:col-span-9 max-w-3xl">
            <p className="serif text-[22px] md:text-[28px] leading-[1.35] text-[#1A1A1A] font-medium whitespace-pre-line" data-testid="insight-lede">
              {body?.lede}
            </p>

            {/* Mobile expand-all */}
            <div className="mt-8 lg:hidden flex justify-end">
              <button
                type="button"
                onClick={allOpen ? collapseAll : expandAll}
                className="inline-flex items-center gap-1.5 mono text-[10px] tracking-[0.2em] uppercase px-3 py-2 rounded-full bg-[#F4F2EC] hover:bg-[#E8E4D9] text-[#1A1A1A]/70"
                data-testid="expand-all-mobile-btn"
              >
                {allOpen ? <><Minimize size={12} /> Collapse all</> : <><Expand size={12} /> Expand all</>}
              </button>
            </div>

            <div className="mt-10 md:mt-14 border-b border-[#E8E4D9]" data-testid="chapters-container">
              {chapters.map((ch, i) => (
                <ChapterAccordion
                  key={i}
                  chapter={ch}
                  index={i}
                  chapterId={`chapter-${i}`}
                  open={openSet.has(i)}
                  onToggle={() => toggle(i)}
                />
              ))}
            </div>

            {body?.pullQuote && (
              <div className="mt-16 rounded-[20px] bg-[#F4F2EC] border border-[#E8E4D9] p-8 md:p-10">
                <Quote size={22} className="text-[#22A94F]" />
                <div className="serif text-[26px] md:text-[32px] leading-[1.2] text-[#1A1A1A] mt-4">“{body.pullQuote}”</div>
                <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#1A1A1A]/50 mt-4">DigitalWorks Editorial</div>
              </div>
            )}

            {/* NeuralWorks cross-nav */}
            <div className="mt-16 rounded-[24px] bg-[#1A1A1A] text-[#FBFAF6] p-8 md:p-10">
              <div className="grid grid-cols-12 gap-6 items-center">
                <div className="col-span-12 md:col-span-8">
                  <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#22A94F] mb-3">How we take ideas to outcomes</div>
                  <h3 className="serif text-[26px] md:text-[32px] leading-tight">{body?.nwLink}</h3>
                  <p className="text-[14px] text-[#FBFAF6]/70 mt-3">NeuralWorks is one of the frameworks in our toolkit &mdash; combined with our teams&rsquo; delivery experience, it helps us build AI-native outcomes with the appropriate discipline.</p>
                </div>
                <div className="col-span-12 md:col-span-4 flex md:justify-end">
                  <Link to="/neuralworks" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#22A94F] text-[#0F3D22] text-[13px] font-semibold hover:bg-[#7ED4A0] transition-colors" data-testid="neuralworks-cta">
                    Explore NeuralWorks <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* More insights */}
      <section className="py-16 bg-[#F4F2EC] border-t border-[#E8E4D9]">
        <div className="dwx">
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#1A1A1A]/55">More from DigitalWorks</div>
          <h3 className="serif-tight text-[36px] md:text-[48px] leading-[1] mt-3">Continue reading.</h3>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
            {others.map((p) => (
              <Link key={p.slug} to={`/insights/${p.slug}`} className="group rounded-[18px] bg-[#FBFAF6] p-6 border border-[#E8E4D9] lift" data-testid={`more-insight-${p.slug}`}>
                <div className="h-32 bg-[#F4F2EC] rounded-[12px] p-4 mb-5"><InsightMotif variant={p.motif} tone="#22A94F" /></div>
                <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#22A94F]">{p.category}</div>
                <div className="serif text-[20px] leading-tight mt-2">{p.title}</div>
                <div className="mt-3 text-[13px] text-[#1A1A1A]/65 line-clamp-2">{p.excerpt}</div>
                <div className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-medium text-[#178838] group-hover:text-[#22A94F]">Read <ArrowRight size={14} /></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ClosingCTA />
    </div>
  );
}
